import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { useParams, useLocation } from 'react-router';
import { 
    Box, 
    styled, 
    Container, 
    Typography, 
    FormControl, 
    Select, 
    MenuItem, 
    TextField,
    InputAdornment,
    Paper
} from '@mui/material';
import axios from '../../../utils/axios';
import useAuth from '../../../hooks/useAuth';
import { dispatch } from '../../../redux/store';
import { getUserBalanceSummary, getUserStatistics } from '../../../redux/slices/operator';
import useTrade from '../../../hooks/useTrade';
import Page from '../../../components/Page';
import HeaderBreadcrumbs from '../../../components/HeaderBreadcrumbs';
import useSettings from '../../../hooks/useSettings';
import DashboardCardsPanel from './DashboardCardsPanel';
import DashboardTablePanel from './DashboardTablePanel';

// Styled components - Side by side layout: 45% left (broker data) + 55% right (overview cards)
const MainContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
    padding: 0,
    width: '100%',
    minHeight: 'calc(100vh - 200px)',
    backgroundColor: 'transparent',
    alignItems: 'stretch',
    [theme.breakpoints.down('md')]: {
        flexDirection: 'column',
        gap: theme.spacing(1.5),
        minHeight: 'auto',
    },
}));

// Container for broker data table/cards - LEFT side (45%)
const BrokerDataSection = styled(Box)(({ theme }) => ({
    flex: '0 0 45%',
    width: '45%',
    backgroundColor: 'transparent',
    borderRadius: 0,
    border: 'none',
    boxShadow: 'none',
    padding: 0,
    margin: 0,
    overflow: 'hidden',
    [theme.breakpoints.down('md')]: {
        flex: '0 0 100%',
        width: '100%',
        marginBottom: theme.spacing(2),
    },
}));

// Container for overview cards - RIGHT side (55%)
const OverviewCardsSection = styled(Box)(({ theme }) => ({
    flex: '0 0 55%',
    width: '55%',
    backgroundColor: 'transparent',
    borderRadius: 0,
    border: 'none',
    boxShadow: 'none',
    padding: 0,
    margin: 0,
    overflow: 'auto',
    [theme.breakpoints.down('md')]: {
        flex: '0 0 100%',
        width: '100%',
    },
}));

const Dashboard = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [userRole, setUserRole] = useState(null);
    const [userName, setUserName] = useState('');
    const [filterName, setFilterName] = useState('');
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('username');
    
    // Currency-related states - read from localStorage with fallback to 'INR'
    const [currency, setCurrency] = useState(localStorage.getItem('currency') || 'INR');
    const [convertToINR, setConvertToINR] = useState(false);
    const [usdPrice, setUsdPrice] = useState(85); // Will be fetched from API
    const [variation, setVariation] = useState(0);

    // Initialize state from localStorage for persistence
    // Fetch USD price from API
    useEffect(() => {
        const getUsdPrice = async () => {
            try {
                const response = await axios.get('/script/usd-price');
                const inrRate = response.data?.usdPrice || 85; // Fallback value
                setUsdPrice(inrRate);
            } catch (error) {
                console.error('Error fetching USD price:', error);
                setUsdPrice(85); // Fallback value
            }
        };
        getUsdPrice();
    }, []);

    // Save currency selection to localStorage when it changes
    useEffect(() => {
        localStorage.setItem('currency', currency);
    }, [currency]);

    const getStoredSelectedBroker = () => {
        try {
            const stored = localStorage.getItem('dashboardSelectedBroker');
            return stored ? JSON.parse(stored) : null;
        } catch {
            return null;
        }
    };

    const getStoredSelectedAdmin = () => {
        try {
            const stored = localStorage.getItem('dashboardSelectedAdmin');
            return stored ? JSON.parse(stored) : null;
        } catch {
            return null;
        }
    };

    const [selectedBroker, setSelectedBroker] = useState(getStoredSelectedBroker);
    const [selectedAdmin, setSelectedAdmin] = useState(getStoredSelectedAdmin);

    // Flag to determine view type based on user role and selections
    const { user } = useAuth();

    // Save to localStorage whenever selectedBroker or selectedAdmin changes
    useEffect(() => {
        if (selectedBroker) {
            localStorage.setItem('dashboardSelectedBroker', JSON.stringify(selectedBroker));
        } else {
            localStorage.removeItem('dashboardSelectedBroker');
        }
    }, [selectedBroker]);

    useEffect(() => {
        if (selectedAdmin) {
            localStorage.setItem('dashboardSelectedAdmin', JSON.stringify(selectedAdmin));
        } else {
            localStorage.removeItem('dashboardSelectedAdmin');
        }
    }, [selectedAdmin]);

    // Validate stored selections on mount - clear if user role doesn't match
    useEffect(() => {
        if (user?.role?.rolename) {
            // If user is not superAdmin, clear selectedAdmin
            if (user.role.rolename !== 'superAdmin' && selectedAdmin) {
                setSelectedAdmin(null);
                localStorage.removeItem('dashboardSelectedAdmin');
            }
            // If user is broker, clear selectedBroker (brokers can't view other brokers)
            if (user.role.rolename === 'broker' && selectedBroker) {
                setSelectedBroker(null);
                localStorage.removeItem('dashboardSelectedBroker');
            }
        }
    }, [user?.role?.rolename]); // Only run when user role changes
    // If logged-in user is a broker, we're always in broker view
    const isBrokerView = user?.role?.rolename === 'broker' || !!selectedBroker;
    // If logged-in user is a superAdmin and has selected an admin, we're in admin view
    const isAdminView = user?.role?.rolename === 'superAdmin' && !!selectedAdmin && !selectedBroker;
    const { userId: userIdFromParams } = useParams();
    const { socket } = useTrade();
    const ClientsData = useSelector((state) => state.operator);
    const { themeStretch } = useSettings();
    const location = useLocation();
    const userId = userIdFromParams || user?.id;
    const tableData = ClientsData?.userbalanceSummary?.docs || [];

    // Use the summary data directly as balanceDetail - no transformation needed
    // The summary object from the API already has the structure expected by the components:
    // summary.NSE.activeProfitLoss.broker, summary.MCX.brokerage.total, etc.
    const balanceDetail = ClientsData?.userMoneyDetails || {};
    const userCounts = ClientsData?.userStatistics;

    // Debug logs to see what data we're receiving
    useEffect(() => {
    }, [ClientsData?.userMoneyDetails, balanceDetail, userCounts, tableData?.length]);

    useEffect(() => {

        async function fetchUserDetails() {
            let currUser;
            try {
                if (userId) {
                    const response = await axios.get(`/user/profile/${userId}`);
                    currUser = response.data.user;
                    setUserRole(currUser.role.rolename);
                    setUserName(currUser.username);
                } else {
                    currUser = user;
                    setUserName(currUser.username);
                    setUserRole(currUser.role.rolename);
                }
            } catch (error) {
                console.error('Error fetching user details:', error);
                enqueueSnackbar('Failed to fetch user details', { variant: 'error' });
            }
        }
        fetchUserDetails();

        // Determine the target ID based on SuperAdmin hierarchy
        let targetId;
        if (selectedBroker) {
            // Viewing a specific broker's clients
            targetId = selectedBroker.id;
        } else if (selectedAdmin) {
            // Viewing a specific admin's brokers
            targetId = selectedAdmin.id;
        } else {
            // Default: viewing user's own data
            targetId = userId || user?._id;
        }        // Fetch balance summary
        let viewContext;
        if (selectedBroker) {
            viewContext = `broker: ${selectedBroker.username}`;
        } else if (selectedAdmin) {
            viewContext = `admin: ${selectedAdmin.username}`;
        } else if (user?.role?.rolename === 'superAdmin') {
            viewContext = 'superAdmin';
        } else {
            viewContext = 'admin';
        }

        dispatch(
            getUserBalanceSummary({
                userId: targetId,
                pageSize: 10, 
                pageNumber: 1,
                filterStatus: 'all',
                sortOrder: order,
                sortBy: orderBy,
                searchWord: filterName,
                currency,
                convertToINR,
                usdPrice,
                variation,
            })
        );

    }, [userId, enqueueSnackbar, user, order, orderBy, filterName, dispatch, selectedBroker, selectedAdmin, currency, convertToINR, usdPrice, variation]);

    // Separate useEffect for getUserStatistics - only call on initial fetch or userId change
    useEffect(() => {
        // Determine the target ID based on SuperAdmin hierarchy
        let targetId;
        if (selectedBroker) {
            targetId = selectedBroker.id;
        } else if (selectedAdmin) {
            targetId = selectedAdmin.id;
        } else {
            targetId = userId || user?._id;
        }

        // Fetch user statistics (static API - only call when targetId changes)
        if (targetId) {
            dispatch(getUserStatistics(targetId));
        }

    }, [userId, user?._id, selectedBroker?.id, selectedAdmin?.id, dispatch]);

    // Function to refresh data after user updates (like balance changes)
    const handleUserUpdate = () => {
        // Determine the target ID based on SuperAdmin hierarchy
        let targetId;
        if (selectedBroker) {
            targetId = selectedBroker.id;
        } else if (selectedAdmin) {
            targetId = selectedAdmin.id;
        } else {
            targetId = userId || user?._id;
        }

        // Refresh balance summary data
        dispatch(
            getUserBalanceSummary({
                userId: targetId,
                pageSize: 10,
                pageNumber: 1,
                filterStatus: 'all',
                sortOrder: order,
                sortBy: orderBy,
                searchWord: filterName,
                currency,
                convertToINR,
                usdPrice,
                variation,
            })
        );
    };

    // WebSocket subscription for real-time updates using Socket.IO
    useEffect(() => {
        const sendDashboardSubscription = () => {
            // Determine the target ID based on SuperAdmin hierarchy
            let targetId;
            if (selectedBroker) {
                targetId = selectedBroker.id;
            } else if (selectedAdmin) {
                targetId = selectedAdmin.id;
            } else {
                targetId = userId || user?._id;
            }

            const message = {
                page: 1,
                pageSize: 10,
                sortBy: orderBy,
                sortOrder: order,
                id: targetId,
                search: filterName,
                currency,
                convertToINR,
                usdPrice,
                variation,
            };

            if (socket && socket.connected) {
                let viewContext;
                if (selectedBroker) {
                    viewContext = `broker: ${selectedBroker.username}`;
                } else if (selectedAdmin) {
                    viewContext = `admin: ${selectedAdmin.username}`;
                } else if (user?.role?.rolename === 'superAdmin') {
                    viewContext = 'superAdmin';
                } else {
                    viewContext = 'admin';
                }
                socket.emit('dashboard_subscribe', message);
            }
        };

        if (socket) {
            // Send subscription when socket is connected
            if (socket.connected) {
                sendDashboardSubscription();
            }

            // Cleanup: unsubscribe when component unmounts or dependencies change
            return () => {
                if (socket && socket.connected) {
                    socket.emit('dashboard_unsubscribe');
                }
            };
        }
    }, [socket, userId, user, orderBy, order, filterName, selectedBroker, selectedAdmin, currency, convertToINR, usdPrice, variation]);

    // Handle username click with SuperAdmin hierarchy support
    const handleUsernameClick = (userId) => {
        if (!userId) {
            console.error('No user ID provided for click handler');
            return;
        }

        // Handle SuperAdmin hierarchy navigation
        if (user?.role?.rolename === 'superAdmin') {
            if (selectedBroker?.id === userId) {
                // Clicking on current broker - go back to admin view
                setSelectedBroker(null);

                dispatch(
                    getUserBalanceSummary({
                        userId: selectedAdmin?.id || user?._id,
                        pageSize: 10,
                        pageNumber: 1,
                        filterStatus: 'all',
                        sortOrder: order,
                        sortBy: orderBy,
                        searchWord: filterName,
                        currency,
                        convertToINR,
                        usdPrice,
                        variation,
                    })
                );

                enqueueSnackbar(`Returned to ${selectedAdmin ? `${selectedAdmin.username}'s` : 'super admin'} view`, { variant: 'info' });
            } else if (selectedAdmin && !selectedBroker) {
                // In admin view, clicking on broker
                const broker = tableData.find(item => item.trader?._id === userId);
                if (broker) {
                    setSelectedBroker({
                        id: broker.trader._id,
                        username: broker.trader.username
                    });

                    dispatch(
                        getUserBalanceSummary({
                            userId: broker.trader._id,
                            pageSize: 10,
                            pageNumber: 1,
                            filterStatus: 'all',
                            sortOrder: order,
                            sortBy: orderBy,
                            searchWord: filterName,
                            currency,
                            convertToINR,
                            usdPrice,
                            variation,
                        })
                    );

                    enqueueSnackbar(`Viewing ${broker.trader.username}'s clients`, { variant: 'success' });
                }
            } else if (!selectedAdmin) {
                // In superAdmin view, clicking on admin
                const admin = tableData.find(item => item.trader?._id === userId);
                if (admin) {
                    setSelectedAdmin({
                        id: admin.trader._id,
                        username: admin.trader.username
                    });

                    dispatch(
                        getUserBalanceSummary({
                            userId: admin.trader._id,
                            pageSize: 10,
                            pageNumber: 1,
                            filterStatus: 'all',
                            sortOrder: order,
                            sortBy: orderBy,
                            searchWord: filterName,
                            currency,
                            convertToINR,
                            usdPrice,
                            variation,
                        })
                    );

                    enqueueSnackbar(`Viewing ${admin.trader.username}'s brokers`, { variant: 'success' });
                }
            }
        } else if (user?.role?.rolename === 'admin' || user?.role?.rolename === 'broker') {
            // Handle Admin/Broker logic (existing functionality)
            if (selectedBroker?.id === userId) {
                setSelectedBroker(null);

                dispatch(
                    getUserBalanceSummary({
                        userId: user?._id,
                        pageSize: 10,
                        pageNumber: 1,
                        filterStatus: 'all',
                        sortOrder: order,
                        sortBy: orderBy,
                        searchWord: filterName,
                        currency,
                        convertToINR,
                        usdPrice,
                        variation,
                    })
                );

                enqueueSnackbar('Returned to admin view', { variant: 'info' });
            } else {
                const broker = tableData.find(item => item.trader?._id === userId);

                if (broker) {
                    setSelectedBroker({
                        id: broker.trader._id,
                        username: broker.trader.username
                    });

                    dispatch(
                        getUserBalanceSummary({
                            userId: broker.trader._id,
                            pageSize: 10,
                            pageNumber: 1,
                            filterStatus: 'all',
                            sortOrder: order,
                            sortBy: orderBy,
                            searchWord: filterName,
                            currency,
                            convertToINR,
                            usdPrice,
                            variation,
                        })
                    );

                    enqueueSnackbar(`Viewing ${broker.trader.username}'s dashboard`, { variant: 'success' });
                } else {
                    console.error('User not found in table data');
                    enqueueSnackbar('User data not found', { variant: 'error' });
                }
            }
        }
    };

    return (
        <Page title="Dashboard">
            <Container maxWidth={false} sx={{ px: 2, width: '100%' }}>
                {/* SuperAdmin breadcrumb navigation */}
                {user?.role?.rolename === 'superAdmin' && (selectedAdmin || selectedBroker) && (
                    <Box sx={{ mb: 1 }}>
                        {/* Admin level breadcrumb */}
                        {selectedAdmin && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    mb: 0.5,
                                    p: 0.5,
                                    bgcolor: 'secondary.lighter',
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'secondary.light'
                                }}
                            >
                                <Box
                                    component="button"
                                    onClick={() => {
                                        setSelectedAdmin(null);
                                        setSelectedBroker(null);
                                        dispatch(
                                            getUserBalanceSummary({
                                                userId: user?._id,
                                                pageSize: 10,
                                                pageNumber: 1,
                                                filterStatus: 'all',
                                                sortOrder: order,
                                                sortBy: orderBy,
                                                searchWord: filterName,
                                                currency,
                                                convertToINR,
                                                usdPrice,
                                                variation,
                                            })
                                        );
                                        enqueueSnackbar('Returned to SuperAdmin view', { variant: 'info' });
                                    }}
                                    sx={{
                                        cursor: 'pointer',
                                        bgcolor: 'secondary.main',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: 1,
                                        py: 0.75,
                                        px: 2,
                                        '&:hover': {
                                            bgcolor: 'secondary.dark',
                                        }
                                    }}
                                >
                                    Return to SuperAdmin View
                                </Box>
                                <Typography variant="subtitle1" sx={{ textTransform: 'uppercase' }}>
                                    Viewing Admin: <strong>{selectedAdmin.username}</strong>
                                    {selectedBroker ? ` → Broker: ${selectedBroker.username}` : "'s brokers"}
                                </Typography>
                            </Box>
                        )}

                        {/* Broker level breadcrumb */}
                        {selectedBroker && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    mb: 0.5,
                                    p: 0.5,
                                    bgcolor: 'primary.lighter',
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'primary.light'
                                }}
                            >
                                <Box
                                    component="button"
                                    onClick={() => handleUsernameClick(selectedBroker.id)}
                                    sx={{
                                        cursor: 'pointer',
                                        bgcolor: 'primary.main',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: 1,
                                        py: 0.75,
                                        px: 2,
                                        '&:hover': {
                                            bgcolor: 'primary.dark',
                                        }
                                    }}
                                >
                                    Return to Admin View
                                </Box>
                                <Typography variant="subtitle1" sx={{ textTransform: 'uppercase' }}>
                                    Viewing Broker: <strong>{selectedBroker.username}</strong>'s clients
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}

                {/* Admin breadcrumb navigation (for non-SuperAdmin users) */}
                {user?.role?.rolename !== 'superAdmin' && selectedBroker && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 0.5,
                            p: 0.5,
                            bgcolor: 'primary.lighter',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'primary.light'
                        }}
                    >
                        <Box
                            component="button"
                            onClick={() => handleUsernameClick(selectedBroker.id)}
                            sx={{
                                cursor: 'pointer',
                                bgcolor: 'primary.main',
                                color: 'white',
                                border: 'none',
                                borderRadius: 1,
                                py: 0.75,
                                px: 2,
                                '&:hover': {
                                    bgcolor: 'primary.dark',
                                }
                            }}
                        >
                            Return to Admin View
                        </Box>
                        <Typography variant="subtitle1" sx={{ textTransform: 'uppercase' }}>
                            Viewing dashboard for broker: <strong>{selectedBroker.username}</strong>
                        </Typography>
                    </Box>
                )}
                <MainContainer>
                    {/* Broker Data Section - LEFT side (45%) */}
                    <BrokerDataSection>
                        <DashboardTablePanel
                            tableData={tableData}
                            onUsernameClick={handleUsernameClick}
                            onUserUpdate={handleUserUpdate}
                            selectedBroker={selectedBroker}
                            selectedAdmin={selectedAdmin}
                            isBrokerView={isBrokerView}
                            isAdminView={isAdminView}
                            currency={currency}
                            setCurrency={setCurrency}
                            convertToINR={convertToINR}
                            setConvertToINR={setConvertToINR}
                            usdPrice={usdPrice}
                            setUsdPrice={setUsdPrice}
                            variation={variation}
                            setVariation={setVariation}
                        />
                    </BrokerDataSection>

                    {/* Overview Cards Section - RIGHT side (55%) */}
                    <OverviewCardsSection>
                        <DashboardCardsPanel
                            balanceDetail={balanceDetail}
                            userCounts={userCounts}
                            onUsernameClick={handleUsernameClick}
                            selectedBroker={selectedBroker}
                            selectedAdmin={selectedAdmin}
                            isBrokerView={isBrokerView}
                            isAdminView={isAdminView}
                        />
                    </OverviewCardsSection>
                </MainContainer>
            </Container>
        </Page>
    );
};

export default Dashboard;